import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Grid } from '@mui/material';
import { ControlledField, DistanceUnit } from 'remote/global';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import PricingCalculatorTable from 'components/tables/PricingCalculatorTable';
import { useOperatorsCalculatorMutation } from 'api/queries/operators';
import { useOperatorCurrency, useOperatorDistanceUnit } from '@/api/queries/users';
import { PricingCalculatorSchemaType, pricingCalculatorSchema } from './types';
import { EndAdornment } from '../form-helpers';
import { CarrierVehicleClassPricingFields } from './CarrierPricingCostsForm';

type Props = {
  defaultValues?: Partial<PricingCalculatorSchemaType>;
};

const formPresetDefaultValues: Partial<PricingCalculatorSchemaType> = {
  mileage: 30,
  days: 0,
  hours: 1,
  numberOfDrivers: 1,
  common: {
    dailyAllowance: 50,
    fuelPriceLiterExclVat: 1.5,
    basicRatePctSmall: 0,
    basicRatePctLarge: 0,
    executiveRatePctLarge: 0,
    executiveRatePctSmall: 0,
  },
  otherCostsExclVat: 0,
  tollRoadsCostsExclVat: 0,
};

function PricingCalculatorForm(props: Props) {
  const { defaultValues } = props;
  const tInstance = useTranslation();
  const { t } = tInstance;
  const currency = useOperatorCurrency();
  const distanceUnit = useOperatorDistanceUnit();
  const calculatorMutation = useOperatorsCalculatorMutation();
  const calculatorResponse = calculatorMutation.data;

  const formInstance = useForm<PricingCalculatorSchemaType>({
    defaultValues: {
      days: '' as any,
      hours: '' as any,
      mileage: '' as any,
      numberOfDrivers: '' as any,
      otherCostsExclVat: '' as any,
      tollRoadsCostsExclVat: '' as any,
      common: {
        dailyAllowance: '' as any,
        fuelPriceLiterExclVat: '' as any,
        ...formPresetDefaultValues?.common,
        ...defaultValues?.common,
      },
      ...formPresetDefaultValues,
      ...defaultValues,
    },
    resolver: zodResolver(pricingCalculatorSchema),
  });
  const {
    handleSubmit,
    control,
    formState: { isSubmitting, isValidating, isLoading },
  } = formInstance;

  const onValid = async (data: PricingCalculatorSchemaType) => calculatorMutation.mutateAsync(data);

  return (
    <form onSubmit={handleSubmit(onValid)} style={{ width: '100%' }}>
      <Grid container gap={3}>
        <Grid container>
          <Grid container gap={2}>
            <Grid container item sm={4} md={2}>
              <ControlledField
                control={control}
                name='mileage'
                translationKey={
                  distanceUnit === DistanceUnit.KM ? 'calculatorMileageKm' : 'calculatorMileageMi'
                }
                focusInitial
              />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <ControlledField control={control} name='days' />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <ControlledField control={control} name='hours' />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <ControlledField control={control} name='numberOfDrivers' />
            </Grid>
          </Grid>
          <Grid container item xs={12} gap={2}>
            <Grid item xs={12} sm={4} md={2}>
              <ControlledField
                control={control}
                name='common.dailyAllowance'
                fieldProps={{
                  InputProps: {
                    endAdornment: <EndAdornment text={currency} />,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <ControlledField
                control={control}
                name='common.fuelPriceLiterExclVat'
                fieldProps={{
                  InputProps: {
                    endAdornment: <EndAdornment text={currency} />,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <ControlledField
                control={control}
                name='tollRoadsCostsExclVat'
                fieldProps={{
                  InputProps: {
                    endAdornment: <EndAdornment text={currency} />,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <ControlledField
                control={control}
                name='otherCostsExclVat'
                fieldProps={{
                  InputProps: {
                    endAdornment: <EndAdornment text={currency} />,
                  },
                }}
              />
            </Grid>
          </Grid>
          <Grid container item sx={{ mt: 2 }}>
            <CarrierVehicleClassPricingFields form={formInstance} />
          </Grid>
        </Grid>
        <Button
          type='submit'
          variant='contained'
          disabled={isSubmitting || isValidating || isLoading}
          fullWidth
          sx={{ maxWidth: 'max-content', display: 'flex', ml: 'auto' }}
        >
          {t('PricingView.actions.calculate')}
        </Button>
        <PricingCalculatorTable result={calculatorResponse} />
      </Grid>
    </form>
  );
}

export default PricingCalculatorForm;
